import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link2, AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, X } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatApiError } from '@/utils/apiError';
import LinkToXeroButton from '@/components/pricing/LinkToXeroButton';
import { StockProductPicker } from '@/components/pricing/StockProductPicker';
import { enumerateProEpoxyItems, PROEPOXY_CATEGORY_LABEL } from '@/components/proepoxy/proepoxyItemCatalogue';
import { loadProEpoxyCatalogue } from '@/components/proepoxy/catalogueLoader';

const STOCK_CATEGORY_FOR = { floor_prep: 'Floor Preparation & Levelling', primer: 'Epoxy Primers (DPM Primer)', design_coat: 'Epoxy Design System', pigment: 'Metallic Pigments', top_coat: 'Polyaspartic & T2 Top Coats', high_build: 'High Build Coatings' };
const money = (n) => `£${Number(n || 0).toFixed(2)}`;

/** Map every ProEpoxy builder product/pack onto a stock product so orders get a Xero ItemCode + cost. */
export function ProEpoxyItemLinks({ onChanged }) {
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(true);
  const [filter, setFilter] = useState('unmapped');
  const [q, setQ] = useState('');

  const loadProducts = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/stock/products`, { credentials: 'include' });
    if (res.ok) setProducts(await res.json());
  }, []);

  const register = useCallback(async () => {
    await loadProEpoxyCatalogue();
    const res = await fetch(`${API_URL}/api/proepoxy/item-links/register`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: enumerateProEpoxyItems() }) });
    if (res.ok) setData(await res.json());
  }, []);

  useEffect(() => { register(); loadProducts(); }, [register, loadProducts]);

  const link = async (key, product_id) => {
    const res = await fetch(`${API_URL}/api/proepoxy/item-links/link`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ key, product_id }) });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { toast.error(formatApiError(d) || 'Link failed'); return; }
    toast.success(product_id ? 'Mapped to stock product' : 'Mapping removed');
    const r = await fetch(`${API_URL}/api/proepoxy/item-links`, { credentials: 'include' });
    if (r.ok) setData(await r.json());
    onChanged?.();
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const t = q.trim().toLowerCase();
    return data.items.filter((r) => (filter === 'unmapped' ? !r.product_id : filter === 'noxero' ? r.product_id && !r.xero_linked : filter === 'nocost' ? r.product_id && r.unit_cost == null : true))
      .filter((r) => !t || `${r.product_name} ${r.pack_label || ''} ${r.stock_name || ''}`.toLowerCase().includes(t));
  }, [data, filter, q]);

  const grouped = useMemo(() => { const g = new Map(); rows.forEach((r) => { if (!g.has(r.category)) g.set(r.category, []); g.get(r.category).push(r); }); return [...g.entries()]; }, [rows]);

  if (!data) return null;
  const allGood = data.unmapped === 0 && data.not_in_xero === 0 && data.no_cost === 0;

  return (
    <Card className={`p-4 ${allGood ? 'border-emerald-200' : 'border-purple-300 bg-purple-50/30'}`} data-testid="proepoxy-item-links">
      <div className="flex flex-wrap items-center gap-3">
        {allGood ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <AlertTriangle className="w-5 h-5 text-purple-700" />}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-gray-900">ProEpoxy order builder → stock products</div>
          <div className="text-xs text-gray-600" data-testid="proepoxy-item-links-summary">
            {data.total} builder items · <span className={data.unmapped ? 'text-purple-800 font-semibold' : ''}>{data.unmapped} not mapped</span> · <span className={data.not_in_xero ? 'text-amber-800 font-semibold' : ''}>{data.not_in_xero} mapped but not in Xero</span> · <span className={data.no_cost ? 'text-amber-800 font-semibold' : ''}>{data.no_cost} without a cost</span>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={() => setOpen((o) => !o)} data-testid="proepoxy-item-links-toggle">{open ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}{open ? 'Hide' : 'Show list'}</Button>
      </div>

      {open && (
        <div className="mt-3 space-y-2">
          <div className="flex flex-wrap gap-1.5 text-xs items-center">
            {[['unmapped', `Not mapped (${data.unmapped})`], ['noxero', `Not in Xero (${data.not_in_xero})`], ['nocost', `No cost (${data.no_cost})`], ['all', `All (${data.total})`]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setFilter(k)} className={`px-2.5 py-1 rounded-full border font-semibold ${filter === k ? 'bg-[#7A0B7A] text-white border-transparent' : 'bg-white text-gray-700 border-gray-200'}`} data-testid={`proepoxy-links-filter-${k}`}>{l}</button>
            ))}
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search items…" className="h-8 w-48 ml-auto bg-white" data-testid="proepoxy-links-search" />
          </div>
          {grouped.length === 0 ? <div className="text-sm text-gray-500 py-3">Nothing here — all sorted.</div> : grouped.map(([cat, items]) => (
            <div key={cat} className="bg-white border rounded-lg overflow-hidden">
              <div className="px-3 py-1.5 bg-gray-50 border-b text-xs font-bold text-gray-700">{PROEPOXY_CATEGORY_LABEL[cat] || cat} <span className="text-gray-400 font-normal">· {items.length}</span></div>
              <div className="divide-y max-h-[360px] overflow-y-auto">
                {items.map((r) => (
                  <div key={r.key} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm" data-testid={`proepoxy-link-row-${r.key}`}>
                    <div className="flex-1 min-w-[220px]">
                      <div className="font-semibold text-gray-900">{r.product_name}{r.pack_label && <span className="text-gray-500 font-normal"> · {r.pack_label}</span>}</div>
                      {r.orders > 0 && <div className="text-[10px] text-gray-500">on {r.orders} past order{r.orders === 1 ? '' : 's'}{r.legacy ? ' · old builder name, not produced any more' : ''}</div>}
                    </div>
                    {r.product_id ? (
                      <>
                        <span className="inline-flex items-center gap-1 text-xs bg-purple-50 text-purple-900 border border-purple-200 rounded-full px-2 py-0.5 max-w-[260px] truncate" title={r.stock_name} data-testid={`proepoxy-link-stock-${r.key}`}>{r.stock_name}
                          <button type="button" onClick={() => link(r.key, null)} className="text-purple-400 hover:text-rose-600" aria-label="Remove mapping" data-testid={`proepoxy-link-remove-${r.key}`}><X className="w-3 h-3" /></button>
                        </span>
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${r.unit_cost != null ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>{r.unit_cost != null ? `cost ${money(r.unit_cost)}` : 'no cost — set below'}</span>
                        {r.xero_linked ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-sky-700"><Link2 className="w-3 h-3" /> {r.xero_code || 'linked'}</span>
                          : <LinkToXeroButton product={{ id: r.product_id, name: r.stock_name }} onChanged={() => { link(r.key, r.product_id); loadProducts(); }} />}
                      </>
                    ) : (
                      <StockProductPicker products={products} suggestName={`${r.product_name}${r.pack_label ? ` ${r.pack_label}` : ''}`} suggestCategory={STOCK_CATEGORY_FOR[r.category] || 'Miscellaneous'}
                        onPick={(pid) => link(r.key, pid)} onCreated={(p) => setProducts((ps) => [...ps, p])} testId={`proepoxy-link-picker-${r.key}`} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
